import { CSSProperties } from "react";
import { twMerge } from "tailwind-merge";
import { CloseOutlined } from "@ant-design/icons";
import { useTranslations } from "use-intl";
import Card from "@/shared/data-display/card";
import Button from "@/shared/form/button";
import Form from "@/shared/form/form";
import { AiAssistantChatMessageFragment } from "gql/graphql";
import { useScrollToLastMessage } from "@/modules/ai/features/ask-ai-assistant/model/use-scroll-to-last-message";
import { AssistantInfo } from "@/modules/ai/features/ask-ai-assistant/ui/assistant-info";
import { SendQuestionForm } from "@/modules/ai/features/ask-ai-assistant/ui/send-question-form";
import { ChatMessage } from "@/modules/ai/features/ask-ai-assistant/views/chat-message/chat-message";

export function AiAssistantChat({
  messages,
  answer,
  stage,
  answering,
  ask,
  close,
}: {
  messages: AiAssistantChatMessageFragment[];
  answer: AiAssistantChatMessageFragment;
  stage?: string;
  answering: boolean;
  ask: (question: string) => void;
  close: () => void;
}) {
  const t = useTranslations("AI");

  const anchor = useScrollToLastMessage(
    `${messages.length}-${answer.text.length}-${answer.toolCalls.length}-${stage}`,
  );

  const [form] = Form.useForm();

  return (
    <Card
      size={"small"}
      style={
        {
          "--height": "454px",
          "--body-height":
            "calc(var(--height) - var(--ant-card-header-height-sm))",
          "--history-height": "calc(var(--body-height) * 0.85)",
          "--footer-height": "calc(var(--body-height) - var(--history-height))",
        } as CSSProperties
      }
      title={t("Ask AI")}
      className={"w-[90vw] max-w-[364px] xl:w-[408px] xl:h-[--height]"}
      classNames={{
        body: "!p-0 relative",
      }}
      extra={
        <div className={"flex items-center"}>
          <AssistantInfo />

          <Button type={"text"} onClick={close} icon={<CloseOutlined />} />
        </div>
      }
    >
      <div
        className={twMerge(
          "h-[--history-height]",
          "overflow-auto overscroll-contain px-2 py-4",
        )}
      >
        <div className={"grid gap-y-2"}>
          {messages.map((message) => (
            <ChatMessage key={message.id} message={message} />
          ))}

          {answering && (
            <ChatMessage
              stage={answer.text ? undefined : stage}
              message={answer}
            />
          )}

          <div ref={anchor} />
        </div>
      </div>

      <div
        className={twMerge(
          "h-[--footer-height] flex items-center p-2",
          "border-t border-[--ant-color-border-secondary]",
        )}
      >
        <div className={"w-full"}>
          <SendQuestionForm
            key={"send"}
            form={form}
            loading={answering}
            onFinish={({ question }) => {
              if (!question) {
                return;
              }

              ask(question);
              form.resetFields();
            }}
          />
        </div>
      </div>
    </Card>
  );
}
